import React from 'react';
import styled from 'styled-components';
import { AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { flowsApi } from '../../../services/api';

interface QuotaIndicatorProps {
  userId: string;
}

const QuotaIndicator: React.FC<QuotaIndicatorProps> = ({ userId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['quotaStats', userId],
    queryFn: () => flowsApi.getQuotaStats(userId),
    enabled: !!userId,
    refetchInterval: 60000, // Atualiza a cada 1 minuto
  });

  if (isLoading || !data?.data) {
    return null;
  }

  const stats = data.data;
  const { used, limit, percentage, remaining, alertLevel } = stats;

  const getStatusColor = () => {
    switch (alertLevel) {
      case 'critical':
        return '#ff3b30'; // error
      case 'warning':
        return '#ffaa00'; // warning
      default:
        return '#00ff88'; // success
    }
  };

  const getStatusIcon = () => {
    switch (alertLevel) {
      case 'critical':
        return <AlertCircle size={16} />;
      case 'warning':
        return <AlertTriangle size={16} />;
      default:
        return <CheckCircle size={16} />;
    }
  };

  const getStatusText = () => {
    switch (alertLevel) {
      case 'critical':
        return 'Limite Crítico';
      case 'warning':
        return 'Atenção ao Limite';
      default:
        return 'Quota Saudável';
    }
  };

  return (
    <Container>
      <Header>
        <StatusIcon color={getStatusColor()}>
          {getStatusIcon()}
        </StatusIcon>
        <StatusText color={getStatusColor()}>
          {getStatusText()}
        </StatusText>
      </Header>

      <ProgressBarContainer>
        <ProgressBar percentage={percentage} color={getStatusColor()} />
      </ProgressBarContainer>

      <Stats>
        <StatItem>
          <StatLabel>Usado</StatLabel>
          <StatValue>{used.toLocaleString()}</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>Restante</StatLabel>
          <StatValue>{remaining.toLocaleString()}</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>Limite Diário</StatLabel>
          <StatValue>{limit.toLocaleString()}</StatValue>
        </StatItem>
      </Stats>

      <PercentageText color={getStatusColor()}>
        {percentage.toFixed(1)}% utilizado
      </PercentageText>

      {alertLevel === 'critical' && (
        <AlertMessage>
          ⚠️ Você está próximo do limite diário. Novas requisições podem falhar.
        </AlertMessage>
      )}
    </Container>
  );
};

const Container = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const StatusIcon = styled.div<{ color: string }>`
  color: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusText = styled.span<{ color: string }>`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: 600;
  color: ${({ color }) => color};
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 9999px;
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const ProgressBar = styled.div<{ percentage: number; color: string }>`
  height: 100%;
  width: ${({ percentage }) => percentage}%;
  background: ${({ color }) => color};
  border-radius: 9999px;
  transition: width 0.5s ${({ theme }) => theme.animations.easing.easeInOut};
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StatLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 500;
`;

const StatValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 700;
`;

const PercentageText = styled.div<{ color: string }>`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ color }) => color};
  text-align: center;
  font-weight: 600;
`;

const AlertMessage = styled.div`
  margin-top: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: rgba(255, 59, 48, 0.1);
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: 8px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.error};
  text-align: center;
`;

export default QuotaIndicator;
